import { Box, Heading } from "@chakra-ui/react";
import { motion } from "framer-motion";
import React from "react";

import { Card, LoadingIndicator } from "../../../components";
import { itemAnimation, listAnimation } from "../../../components/Animation";
import {
  CaptureStatusEnum,
  InterviewImportDecisionReason,
  InterviewType,
  ScheduledInterviewCallType,
  ScheduledInterviewListItemFragment,
  ScheduledInterviewListItemV2,
  WhenEnum,
} from "../../graphql";
import ScheduledInterviewsListItem from "../home/Interviews/ScheduledInterviewsListItem";

interface InterviewsListProps {
  loading: boolean;
  scheduledInterviews?: ScheduledInterviewListItemV2[] | null;
}

type MappedScheduledInterview = ScheduledInterviewListItemFragment & {
  captureStatus?: CaptureStatusEnum;
  callId?: string | null;
  isInProgress?: boolean;
  when?: WhenEnum;
};
/**
 * Maps a ScheduledInterviewListItemV2 from ScheduledInterviewsQuery results to a ScheduledInterviewListItemFragment.
 *
 * This function is necessary because the ScheduledInterviewsListItem component
 * expects a ScheduledInterviewListItemFragment object, but the data we receive from
 * the ScheduledInterviewsQuery has a different structure (ScheduledInterviewListItemV2).
 * Since ScheduledInterviewsListItem is used in multiple places throughout the application,
 * it's better to adapt our data to match its expected input rather than modifying the component itself.
 *
 * @param interview - A ScheduledInterviewListItemV2 item from ScheduledInterviewsQuery results
 * @returns A ScheduledInterviewListItemFragment object that can be used with ScheduledInterviewsListItem
 */
const mapToScheduledInterviewListItemFragment = (
  interview: ScheduledInterviewListItemV2
): MappedScheduledInterview => {
  const mappedInterview: MappedScheduledInterview = {
    id: interview.id,
    name: interview.name,
    scheduledStart: interview.scheduledStart,
    scheduledEnd: interview.scheduledEnd,
    scheduledInterviewers:
      interview.interviewers?.map((interviewer) => ({
        id: interviewer.id ?? "",
        recordingDisabled: false,
        name: interviewer.email ?? "",
        user: {
          id: interviewer.id,
          firstName: interviewer.firstName,
          lastName: interviewer.lastName,
          fullName: interviewer.name ?? "",
          email: interviewer.email ?? "",
          profilePicUrl: interviewer.profilePicUrl,
          phoneNumber: interviewer.phoneNumber,
        },
      })) || [],
    isVideo:
      interview?.scheduledInterviewCallType !==
      ScheduledInterviewCallType.Phone,
    interviewType:
      interview?.scheduledInterviewCallType !== ScheduledInterviewCallType.Other
        ? (interview.scheduledInterviewCallType as unknown as InterviewType)
        : undefined,
    importDecision: !!interview.importDecision,
    isImported: !!interview.importDecision,
    importDecisionReason: interview.importDecisionReason
      ? (interview.importDecisionReason as InterviewImportDecisionReason)
      : undefined,
    scheduledType: interview.scheduledType,

    // These are not actually needed in this context. Using default values.
    zoomAppAutoOpenEnabled: false,
    isExcluded: false,
    isInterviewer: false,
    recordingDisabled: false,
    googleMeetMeetings: [],
    teamsNotetakerMeetings: [],

    // Augmented data
    captureStatus: interview.captureStatus ?? undefined,
    // Grab the call with the longest duration
    callId: interview?.calls
      ?.filter((c) => !!c?.duration)
      ?.sort(
        (a, b) => parseInt(a?.duration ?? "0") - parseInt(b?.duration ?? "0")
      )
      .at(-1)?.id,
    // Check if any call is in progress
    isInProgress:
      interview?.calls?.some((call) => call?.status === "IN_PROGRESS") ?? false,
    when: interview?.when ?? undefined,
  };

  // Only add candidate if the required properties exist
  if (interview.candidateId) {
    mappedInterview.candidate = {
      id: interview.candidateId,
      fullName: interview.candidateFullName || "",
      firstName: "",
      lastName: "",
      recordingDisabled: false,
    };
  }

  // Only add position if the required properties exist
  if (interview.positionId) {
    mappedInterview.position = {
      id: interview.positionId,
      displayTitle: interview.positionTitle || "",
    };
  }

  return mappedInterview;
};

const InterviewsList: React.FC<InterviewsListProps> = ({
  loading,
  scheduledInterviews,
}) => {
  return (
    <Card variant="transparent" p={4}>
      {loading && !scheduledInterviews && <LoadingIndicator />}
      {!loading && scheduledInterviews?.length === 0 ? (
        <Box textAlign="center" p={5}>
          <Heading size="md">No scheduled interviews found</Heading>
        </Box>
      ) : (
        <motion.div variants={listAnimation} initial="hidden" animate="visible">
          {scheduledInterviews?.map((interview, index) => (
            <motion.div
              key={interview.id}
              variants={itemAnimation}
              custom={index}
            >
              <ScheduledInterviewsListItem
                showRecordingOutcomeMessage
                showHighlightedInterview={false}
                showInterviewLaunchButton={false}
                scheduledInterview={mapToScheduledInterviewListItemFragment(
                  interview
                )}
                listPosition={index + 1}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </Card>
  );
};

export default React.memo(InterviewsList);
